import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  DashboardActionTypes,
  LoadDashboard,
  LoadDashboardSuccess,
  LoadDashboardFailed,
  LoadDatabaseReports,
  LoadDatabaseReportsSuccess,
  LoadDatabaseReportsFailed,
  LoadSummaryReportsSuccess,
  LoadSummaryReportsFailed,
  SaveDashboad,
  SaveDashboadSuccess,
  SaveDashboadFailed,
  DeleteDashboard,
  DeleteDashboardSuccess,
  DeleteDashboardFailed,
  UpdateDashboard,
  UpdateDashboardSuccess,
  UpdateDashboardFailed,
  LoadSummaryFieldValue,
  LoadSummaryFieldValueSuccess,
  LoadSummaryFieldValueFailed,
  LoadReportsByType,
  LoadReportsByTypeSuccess,
  LoadReportsByTypeFailed,
  CopyDashboard,
  CopyDashboardSuccess,
  LoadDashboards,
  CopyDashboardFailed,
  LoadDashboardsSuccess,
  LoadDashboardsFailed,
} from '../../_actions/settings/dashboards.action';
import { CacheService } from '../../../services/cache.service';
import { AvailableCaches } from '../../_models/ignatius.models';
import { SettingsService } from '../../_services/settings.service';
import {
  getSummaryFields,
  getAllDashboards,
  getDatabaseReports,
  getSummaryReports,
  getSummaryFieldValue,
  getReportsByType,
  getDashboardDetail,
} from '../../_selectors/settings/dashboards.selector';
import {
  LoadSummaryFields,
  LoadSummaryFieldsSuccess,
  LoadSummaryFieldsFailed,
  LoadSummaryReports,
} from '../../_actions/settings/dashboards.action';
import {
  AlertService,
  ToastTypes,
} from 'src/app/shared/services/alert.service';
import { RouteService } from 'src/app/core/services/route.service';
import { Router } from '@angular/router';
import { SummaryField } from '../../_models/settings.models';
import { CopyDashboardResponse } from '../../_models/page.model';

@Injectable()
export class DashboardsEffects {
  constructor(
    private actions: Actions,
    private service: SettingsService,
    private store: Store,
    private cache: CacheService,
    private alert: AlertService,
    private route: RouteService,
    private router: Router
  ) {}

  /**
   * Get Dashboards
   */
  loadDashboards: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadDashboards>(DashboardActionTypes.LOAD_DASHBOARDS),
      withLatestFrom(this.store.pipe(select(getAllDashboards))),
      mergeMap(([action, dashboards]) => {
        if (
          !action.force &&
          dashboards[action.applicationId] &&
          this.cache.getCacheState(AvailableCaches.DASHBOARDS)
        )
          return of(
            new LoadDashboardsSuccess(
              action.applicationId,
              dashboards[action.applicationId]
            )
          );
        return this.service.getDashboards(action.applicationId).pipe(
          map(
            (payload: Array<any>) =>
              new LoadDashboardsSuccess(action.applicationId, payload)
          ),
          catchError((err) => of(new LoadDashboardsFailed(err, action.applicationId)))
        );
      })
    )
  );

  /**
     * Load a Page
     */
    loadDashboard: Observable<Action> = createEffect(() =>
      this.actions.pipe(
        ofType<LoadDashboard>(DashboardActionTypes.LOAD_DASHBOARD),
        withLatestFrom(this.store.pipe(select(getDashboardDetail))),
        mergeMap(([action, dashboards]) => {
          if (
            dashboards[action.applicationId] &&
            dashboards[action.applicationId][action.dashboardId] &&
            this.cache.getCacheState(AvailableCaches.DASHBOARDS)
          )
            return of(
              new LoadDashboardSuccess(
                action.applicationId,
                action.dashboardId,
                dashboards[action.applicationId][action.dashboardId]
              )
            );
          return this.service.getDashboardById(action.dashboardId).pipe(
            map(
              (payload: Array<any>) =>
                new LoadDashboardSuccess(action.applicationId, action.dashboardId, payload)
            ),
            catchError((err) => of(new LoadDashboardFailed(err)))
          );
        })
      )
    );

  /**
   * Get Summary Fields
   */
  loadSummaryFields: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadSummaryFields>(DashboardActionTypes.LOAD_SUMMARY_FIELDS),
      withLatestFrom(this.store.pipe(select(getSummaryFields))),
      mergeMap(([action, summaryFields]) => {
        if (
          summaryFields[action.appId] &&
          this.cache.getCacheState(AvailableCaches.SUMMARY_FIELDS)
        )
          return of(
            new LoadSummaryFieldsSuccess(
              action.appId,
              summaryFields[action.appId]
            )
          );
        return this.service.getSummaryFields(action.appId).pipe(
          map(
            (payload: Array<any>) =>
              new LoadSummaryFieldsSuccess(action.appId, payload)
          ),
          catchError((err) => of(new LoadSummaryFieldsFailed(err, action.appId)))
        );
      })
    )
  );

  /**
   * Get Database Reports
   */
  loadDatabaseReports: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadDatabaseReports>(DashboardActionTypes.LOAD_DATABASE_REPORTS),
      withLatestFrom(this.store.pipe(select(getDatabaseReports))),
      mergeMap(([action, reports]) => {
        if (
          reports[action.appId] &&
          this.cache.getCacheState(AvailableCaches.SUMMARY_FIELDS)
        )
          return of(
            new LoadDatabaseReportsSuccess(action.appId, reports[action.appId])
          );
        return this.service.getReports(action.appId, 'datatable').pipe(
          map(
            (payload: Array<any>) =>
              new LoadDatabaseReportsSuccess(action.appId, payload)
          ),
          catchError((err) => of(new LoadDatabaseReportsFailed(err, action.appId)))
        );
      })
    )
  );

  /**
   * Get Summary Reports
   */
  loadSummaryReports: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadSummaryReports>(DashboardActionTypes.LOAD_SUMMARY_REPORTS),
      withLatestFrom(this.store.pipe(select(getSummaryReports))),
      mergeMap(([action, reports]) => {
        if (
          reports[action.appId] &&
          this.cache.getCacheState(AvailableCaches.SUMMARY_REPORTS)
        )
          return of(
            new LoadSummaryReportsSuccess(action.appId, reports[action.appId])
          );
        return this.service.getReports(action.appId, 'flex').pipe(
          map(
            (payload: Array<any>) =>
              new LoadSummaryReportsSuccess(action.appId, payload)
          ),
          catchError((err) => of(new LoadSummaryReportsFailed(err, action.appId)))
        );
      })
    )
  );

  /**
   * Save Dashboard
   */
  saveDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<SaveDashboad>(DashboardActionTypes.SAVE_DASHBOARD),
      mergeMap((action) => {
        return this.service.saveDashboard(action.payload).pipe(
          map((res: any) => {
            this.alert.toast(
              ToastTypes.SUCCESS,
              'Success',
              'Dashboard is created Successfully!'
            );
            this.route.back();
            const instance = new SaveDashboadSuccess(action.appId, res);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new SaveDashboadFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Update Dashboard
   */
  updateDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<UpdateDashboard>(DashboardActionTypes.UPDATE_DASHBOARD),
      mergeMap((action) => {
        return this.service.updateDashboard(action.payload).pipe(
          map((res: any) => {
            this.alert.toast(
              ToastTypes.SUCCESS,
              'Success',
              'Dashboard is updated Successfully!'
            );
            this.route.back();
            const instance = new UpdateDashboardSuccess( action.appId, action.dashboardId, res);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new UpdateDashboardFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );

  /**
   * Delete Dashboard
   */
  deleteDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<DeleteDashboard>(DashboardActionTypes.DELETE_DASHBOARD),
      mergeMap((action) => {
        return this.service.deleteDashboard(action.dashboardId).pipe(
          map((res: any) => {
            this.alert.toast(
              ToastTypes.SUCCESS,
              'Success',
              'Dashboard is delete Successfully!'
            );
            return new DeleteDashboardSuccess(action.appId, action.dashboardId);
          }),
          catchError((err) => {
            return of(new DeleteDashboardFailed(err));
          })
        );
      })
    )
  );

  /**
   * Reset Dashboard Data
   */
  resetDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<any>(DashboardActionTypes.UPDATE_DASHBOARD_SUCCESS),
      mergeMap((action) => {
        return of(new LoadDashboards(action.appId, true));
      })
    )
  );

  /**
   * Get Summary field value
   */
  loadSummaryFieldValue: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadSummaryFieldValue>(
        DashboardActionTypes.LOAD_SUMMARY_FIELD_VALUE
      ),
      withLatestFrom(this.store.pipe(select(getSummaryFieldValue))),
      mergeMap(([action, value]) => {
        if (
          value[action.appId] &&
          value[action.summaryFieldId] &&
          this.cache.getCacheState(AvailableCaches.SUMMARY_REPORTS) &&
          !action.force
        )
          return of(
            new LoadSummaryFieldValueSuccess(
              action.appId,
              action.summaryFieldId,
              value[action.appId][action.summaryFieldId]
            )
          );
        return this.service.getSummaryFieldValue(action.summaryFieldId).pipe(
          map(
            (payload: SummaryField) =>
              new LoadSummaryFieldValueSuccess(
                action.appId,
                action.summaryFieldId,
                payload
              )
          ),
          catchError((err) => of(new LoadSummaryFieldValueFailed(err)))
        );
      })
    )
  );

  /**
   * Get Reports
   */
  loadReports: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<LoadReportsByType>(DashboardActionTypes.LOAD_REPORTS_BY_TYPE),
      withLatestFrom(this.store.pipe(select(getReportsByType))),
      mergeMap(([action, reports]) => {
        return this.service.getReports(action.appId, action.reportType).pipe(
          map(
            (payload: Array<any>) =>
              new LoadReportsByTypeSuccess(
                action.appId,
                action.reportType,
                payload
              )
          ),
          catchError((err) => of(new LoadReportsByTypeFailed(err, action.appId)))
        );
      })
    )
  );

  /**
   * Copy Dashboard
   */
  copyDashboard: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<CopyDashboard>(DashboardActionTypes.COPY_DASHBOARD),
      mergeMap((action) => {
        return this.service.copyDashboard(action.payload).pipe(
          map((payload: CopyDashboardResponse) => {
            const instance = new CopyDashboardSuccess(payload);
            instance.refId = action.refId;
            return instance;
          }),
          catchError((err) => {
            const instance = new CopyDashboardFailed(err);
            instance.refId = action.refId;
            return of(instance);
          })
        );
      })
    )
  );
}
